import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { LoadingSpinner } from "~/components/Icons";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";

import { DoclistTreeItem } from "~/api/model";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

interface MergeAllCategoryDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  category: DoclistTreeItem;
  onMergeAll: (mergedSectionName: string) => Promise<void>;
  isLoading?: boolean;
}

export const MergeAllCategoryDialog: FC<MergeAllCategoryDialogProps> = ({
  open,
  setOpen,
  category,
  onMergeAll,
  isLoading = false,
}) => {
  const intl = useIntl();

  const [mergedSectionName, setMergedSectionName] = useState("");
  const [mergedSectionError, setMergedSectionError] = useState(false);

  useEffect(() => {
    if (open && category?.sections?.length > 0) {
      setMergedSectionName(category.sections[0].name);
      setMergedSectionError(false);
    } else if (!open) {
      setMergedSectionName("");
      setMergedSectionError(false);
    }
  }, [open, category]);

  const handleBlurMergedSection = () => {
    if (!mergedSectionName.trim()) {
      setMergedSectionError(true);
    } else {
      setMergedSectionError(false);
    }
  };

  const handleMerge = async () => {
    if (!mergedSectionName.trim()) {
      setMergedSectionError(true);
      return;
    }

    trackEvent({
      eventName: "app/doc_list/merge_all_category_dialog/merge.button/click",
    });

    try {
      await onMergeAll(mergedSectionName);
      setOpen(false);
    } catch (error) {
      logger.error("Merge operation failed:", error);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="w-full max-w-[720px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="docList.mergeAllCategoryDialog.title"
              defaultMessage="Merge All Documents"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="docList.mergeAllCategoryDialog.description"
              defaultMessage="This will merge {count} documents in the {categoryName} category into a single document."
              values={{
                count: category?.sections?.length || 0,
                categoryName: category?.category?.name || "",
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-4 max-w-[672px]">
          <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
            <FormattedMessage
              id="docList.mergeAllCategoryDialog.warning"
              defaultMessage="This action is irreversible. All documents will be combined into one."
            />
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-2">
          <Label htmlFor="mergedSectionName">
            <FormattedMessage
              id="docList.mergeAllCategoryDialog.mergedSectionName"
              defaultMessage="Merged Document Name"
            />
          </Label>
          <Input
            id="mergedSectionName"
            type="text"
            value={mergedSectionName}
            onChange={(e) => {
              setMergedSectionName(e.target.value);
              if (mergedSectionError) setMergedSectionError(false);
            }}
            required
            onBlur={handleBlurMergedSection}
            placeholder={intl.formatMessage({
              id: "docList.splitSectionDialog.placeholder",
              defaultMessage: "Enter document name",
            })}
            className={`shadow-none ${mergedSectionError && "border-destructive"}`}
            disabled={isLoading}
          />
          {mergedSectionError && (
            <span className="text-sm text-destructive">
              <FormattedMessage
                id="docList.splitSectionDialog.placeholder"
                defaultMessage="Enter document name"
              />
            </span>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={handleMerge}
            disabled={!mergedSectionName.trim() || isLoading}
            variant="default"
          >
            {isLoading && <LoadingSpinner className="mr-2 h-4 w-4" />}
            <FormattedMessage
              id="docList.mergeAllCategoryDialog.merge"
              defaultMessage="Merge All"
            />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
